<template>
  <a-drawer width="90%" :label-col="4" :wrapper-col="14" :visible="open" :destroyOnClose="true" @close="onClose">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="rules">
      <a-form-model-item :label="$t('views.goods.form.category')" prop="categoryId" >
        <a-select
          v-model="form.categoryId"
          show-search
          allow-clear
          :loading="categoryLoading"
          :placeholder="$t('views.goods.form.categoryPlaceholder')"
          :filter-option="filterCategoryOption"
        >
          <a-select-option
            v-for="item in categoryOptions"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.type')" prop="type">
        <a-select
          v-model="form.type"
          allow-clear
          :placeholder="$t('views.goods.form.typePlaceholder')"
        >
          <a-select-option :value="0">{{ $t('views.goods.type.0') }}</a-select-option>
          <a-select-option :value="1">{{ $t('views.goods.type.1') }}</a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.name')" prop="name" >
        <a-input v-model="form.name" :placeholder="$t('views.goods.form.namePlaceholder')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.nameEn')" prop="nameEn" >
        <a-input v-model="form.nameEn" :placeholder="$t('views.goods.form.nameEnPlaceholder')" />
      </a-form-model-item>


      <a-form-model-item :label="$t('views.goods.form.spec')" prop="specIdList">
        <a-select
          v-model="form.specIdList"
          mode="multiple"
          show-search
          allow-clear
          :loading="specLoading"
          :placeholder="$t('views.goods.form.specPlaceholder')"
          :filter-option="filterSpecOption"
        >
          <a-select-option
            v-for="item in specOptions"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-divider orientation="left" style="margin: 8px 0;">{{ $t('views.goods.form.dividerSpecOptions') }}</a-divider>
      <div v-if="Array.isArray(form.specIdList) && form.specIdList.length">
        <a-card
          v-for="specId in form.specIdList"
          :key="String(specId)"
          size="small"
          class="spec-card"
          style="margin-bottom: 12px;"
        >
          <template slot="title">
            <div class="spec-card-title">
              <div class="spec-card-title__left">
                {{ getSpecName(specId) || specLabelFallback(specId) }}
<!--                <span v-if="getSpecIsCalculation(specId) === 1" class="spec-card-title__calc">参与计算</span>-->
                <span v-if="getSpecIsCustomization(specId) === 1" class="spec-card-title__tag spec-card-title__tag--custom">{{ $t('views.goods.form.tagCustom') }}</span>
                <span v-if="getSpecIdentityRestrictions(specId) === 1" class="spec-card-title__tag spec-card-title__tag--pro">{{ $t('views.goods.form.tagPro') }}</span>
              </div>
              <div class="spec-card-title__right">
                <span class="spec-card-title__label">{{ $t('views.goods.form.sortLabel') }}</span>
                <a-input-number
                  v-model="specSortMap[String(specId)]"
                  :min="0"
                  :step="1"
                  style="width: 110px;"
                />
              </div>
            </div>
          </template>
          <template slot="extra">
            <a-space :size="10" wrap>
              <a-button size="small" style="min-width: 64px;" @click="selectAllSpecOptions(specId)">{{ $t('views.goods.form.selectAll') }}</a-button>
              <a-button size="small" style="min-width: 64px;" @click="clearAllSpecOptions(specId)">{{ $t('views.goods.form.selectNone') }}</a-button>
            </a-space>
          </template>
          <a-spin :spinning="!!specOptionLoadingMap[String(specId)]">
            <div v-if="Array.isArray(specOptionMap[String(specId)]) && specOptionMap[String(specId)].length">
              <a-checkbox-group v-model="specSelectedOptionMap[String(specId)]">
                <div class="spec-option-grid">
                  <div
                    v-for="opt in getSortedSpecOptions(specId)"
                    :key="String(opt.id)"
                    class="spec-option-row"
                    :class="{ 'spec-option-row--checked': isSpecOptionSelected(specId, opt.id) }"
                  >
                    <a-checkbox class="spec-option-row__checkbox" :value="opt.id" />
                    <div class="spec-option-row__body" @click="toggleSpecOption(specId, opt.id)">
                      <div class="spec-option-item">
                        <span class="spec-option-name" :title="opt.name">{{ opt.name }}</span>
                        <span class="spec-option-comb" @click.stop @mousedown.stop @mouseup.stop>
                          <span class="spec-option-comb__label">{{ $t('views.goods.form.combSort') }}</span>
                          <a-input-number
                            v-model="specOptionSortMap[String(specId)][String(opt.id)]"
                            :min="0"
                            :step="1"
                            size="small"
                            style="width: 90px;"
                            @click.native.stop
                            @mousedown.native.stop
                            @mouseup.native.stop
                          />
                          <span class="spec-option-comb__label">{{ $t('views.goods.form.combCombo') }}</span>
                          <a-switch
                            :checked="!!(specOptionCombinationMap[String(specId)] && specOptionCombinationMap[String(specId)][String(opt.id)])"
                            size="small"
                            @change="(v) => onOptionCombinationChange(specId, opt.id, v)"
                            @click.native.stop
                            @mousedown.native.stop
                            @mouseup.native.stop
                          />
                          <template v-if="!!(specOptionCombinationMap[String(specId)] && specOptionCombinationMap[String(specId)][String(opt.id)])">
                            <span class="spec-option-comb__label" style="margin-left: 10px; white-space: nowrap;">{{ $t('views.goods.form.combType') }}</span>
                            <a-select
                              v-model="specOptionTypeMap[String(specId)][String(opt.id)]"
                              size="small"
                              style="width: 110px;"
                              :getPopupContainer="trigger => trigger.parentNode"
                              @click.native.stop.prevent
                              @mousedown.native.stop.prevent
                              @mouseup.native.stop.prevent
                            >
                              <a-select-option :value="0">{{ $t('views.goods.form.combSingle') }}</a-select-option>
                              <a-select-option :value="1">{{ $t('views.goods.form.combMulti') }}</a-select-option>
                            </a-select>
                          </template>
                          <template v-if="!!(specOptionCombinationMap[String(specId)] && specOptionCombinationMap[String(specId)][String(opt.id)]) && getCombinationRows(specId, opt.id).length">
                            <span class="spec-option-comb__label" style="margin-left: 10px; white-space: nowrap;">{{ $t('views.goods.form.combDefault') }}</span>
                            <a-select
                              :value="getDefaultGroupModel(specId, opt.id)"
                              :mode="isDefaultMulti(specId, opt.id) ? 'multiple' : undefined"
                              allow-clear
                              size="small"
                              style="width: 220px;"
                              :placeholder="$t('views.goods.form.optional')"
                              :getPopupContainer="trigger => trigger.parentNode"
                              @change="(v) => onDefaultGroupModelChange(specId, opt.id, v)"
                              @click.native.stop.prevent
                              @mousedown.native.stop.prevent
                              @mouseup.native.stop.prevent
                            >
                              <a-select-option
                                v-for="(r, ridx) in getCombinationRows(specId, opt.id)"
                                :key="getGroupRowKey(r, ridx)"
                                :value="getGroupRowKey(r, ridx)"
                              >
                                {{ $t('views.goods.form.combTitle', { n: ridx + 1 }) }}
                              </a-select-option>
                            </a-select>
                          </template>
                        </span>
                      </div>
                      <div
                        v-if="!!(specOptionCombinationMap[String(specId)] && specOptionCombinationMap[String(specId)][String(opt.id)])"
                        class="spec-option-comb-select"
                        @click.stop.prevent
                        @mousedown.stop.prevent
                        @mouseup.stop.prevent
                      >
                        <div
                          v-for="(row, ridx) in getCombinationRows(specId, opt.id)"
                          :key="combRowVueKey(specId, opt.id, row, ridx)"
                          class="spec-option-comb-row"
                          @click.stop.prevent
                          @mousedown.stop.prevent
                          @mouseup.stop.prevent
                        >
                          <span class="spec-option-comb-row__title">{{ $t('views.goods.form.combTitle', { n: ridx + 1 }) }}</span>
                          <a-select
                            v-model="specOptionCombinationSelectMap[String(specId)][String(opt.id)][ridx].optionIdList"
                            mode="multiple"
                            show-search
                            allow-clear
                            :loading="allSpecOptionLoading"
                            :placeholder="$t('views.goods.form.combPickItems')"
                            :filter-option="filterAllSpecOption"
                            :getPopupContainer="trigger => trigger.parentNode"
                            @click.native.stop.prevent
                            @mousedown.native.stop.prevent
                            @mouseup.native.stop.prevent
                          >
                            <a-select-option
                              v-for="it in allSpecOptionOptions"
                              :key="String(it.id)"
                              :value="it.id"
                            >
                              {{ it.name }}
                            </a-select-option>
                          </a-select>
                          <span class="spec-option-comb__label" style="margin-left: 10px; white-space: nowrap; display: inline-flex; align-items: center; height: 32px;">{{ $t('views.goods.form.combGroupName') }}</span>
                          <a-input
                            :value="getCombRowInputDisplay(specId, opt.id, ridx, 'name')"
                            size="small"
                            style="width: 300px; vertical-align: middle;"
                            :placeholder="$t('views.goods.form.combNamePlaceholder')"
                            allow-clear
                            @change="(e) => onCombRowAInputChangeByPath(specId, opt.id, ridx, 'name', e)"
                            @click.native.stop
                            @mousedown.native.stop
                            @mouseup.native.stop
                          />
                          <span class="spec-option-comb__label" style="margin-left: 10px; white-space: nowrap; display: inline-flex; align-items: center; height: 32px;">{{ $t('views.goods.form.combGroupNameEn') }}</span>
                          <!-- nameEn 用原生 input：a-input 受控 + 深层路径在部分环境下 nameEn 不触发更新；原生 @input 最稳 -->
                          <input
                            type="text"
                            class="ant-input ant-input-sm"
                            style="width: 300px; vertical-align: middle;"
                            :value="getCombRowInputDisplay(specId, opt.id, ridx, 'nameEn')"
                            :placeholder="$t('views.goods.form.combNameEnPlaceholder')"
                            @input="e => onCombRowAInputChangeByPath(specId, opt.id, ridx, 'nameEn', e)"
                            @click.stop
                            @mousedown.stop
                            @mouseup.stop
                          />
                          <span class="spec-option-comb__label" style="margin-left: 10px; white-space: nowrap; display: inline-flex; align-items: center; height: 32px;">{{ $t('views.goods.form.combSort') }}</span>
                          <a-input-number
                            v-model="specOptionCombinationSelectMap[String(specId)][String(opt.id)][ridx].sort"
                            :min="0"
                            :step="1"
                            size="small"
                            style="width: 90px; vertical-align: middle;"
                            @click.native.stop
                            @mousedown.native.stop
                            @mouseup.native.stop
                          />
                          <a-button
                            size="small"
                            type="link"
                            class="spec-option-comb-row__delete"
                            @click.stop="removeCombinationRow(specId, opt.id, ridx)"
                            @mousedown.native.stop
                            @mouseup.native.stop
                          >
                            {{ $t('views.goods.form.combDelete') }}
                          </a-button>
                        </div>
                        <a-button
                          size="small"
                          @click.stop="addCombinationRow(specId, opt.id)"
                          @mousedown.native.stop
                          @mouseup.native.stop
                        >
                          {{ $t('views.goods.form.combAddRow') }}
                        </a-button>
                      </div>
                    </div>
                  </div>
                </div>
              </a-checkbox-group>
              <div class="spec-option-footer">
                {{ $t('views.goods.form.specSelected', { selected: (specSelectedOptionMap[String(specId)] || []).length, total: specOptionMap[String(specId)].length }) }}
              </div>
            </div>
            <a-empty v-else :description="$t('views.goods.form.emptyNoOptions')" />
          </a-spin>
        </a-card>
      </div>
      <a-empty v-else :description="$t('views.goods.form.emptySelectSpec')" />

      <a-divider orientation="left" style="margin: 8px 0;">{{ $t('views.goods.form.dividerAssociation') }}</a-divider>
      <div style="margin-bottom: 12px;">
        <a-button type="dashed" icon="plus" @click="addAssociationRow">{{ $t('views.goods.form.addAssociation') }}</a-button>
      </div>
      <div v-if="!associationList || associationList.length === 0" style="color: rgba(0,0,0,0.45); margin-bottom: 12px;">
        {{ $t('views.goods.form.associationHint') }}
      </div>
      <a-card
        v-for="(a, aidx) in associationList"
        :key="a._rowKey"
        size="small"
        :title="$t('views.goods.form.associationCardTitle', { n: aidx + 1 })"
        style="margin-bottom: 12px;"
      >
        <template slot="extra">
          <a-button type="link" style="padding: 0;" @click="removeAssociationRow(aidx)">{{ $t('views.goods.form.delete') }}</a-button>
        </template>
        <a-row :gutter="12">
          <a-col :span="8">
            <a-form-model-item :label="$t('views.goods.form.assocSpec')">
              <a-select
                v-model="a.specId"
                show-search
                allow-clear
                :placeholder="$t('views.goods.form.assocSpecPlaceholder')"
                :filter-option="filterSpecOption"
                @change="() => onAssociationSpecChange(a)"
              >
                <a-select-option v-for="sid in (form.specIdList || [])" :key="String(sid)" :value="sid">
                  {{ getSpecName(sid) || specLabelFallback(sid) }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="8">
            <a-form-model-item :label="$t('views.goods.form.assocOption')">
              <a-select
                v-model="a.specOptionId"
                show-search
                allow-clear
                :placeholder="$t('views.goods.form.assocOptionPlaceholder')"
                :filter-option="filterAllSpecOption"
                @change="() => onAssociationSpecOptionChange(a)"
              >
                <a-select-option
                  v-for="opt in getSelectedSpecOptionChoices(a.specId)"
                  :key="String(opt.id)"
                  :value="String(opt.id)"
                >
                  {{ opt.name }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="8">
            <a-form-model-item :label="$t('views.goods.form.assocTwoSpec')">
              <a-select
                v-model="a.twoSpecId"
                show-search
                allow-clear
                :placeholder="$t('views.goods.form.assocTwoSpecPlaceholder')"
                :filter-option="filterSpecOption"
                @change="() => onAssociationTwoSpecChange(a)"
              >
                <a-select-option
                  v-for="sid in getAssociationTwoSpecChoices(a.specId)"
                  :key="String(sid)"
                  :value="sid"
                >
                  {{ getSpecName(sid) || specLabelFallback(sid) }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="12">
          <a-col :span="16">
<!--            {{ typeof (getSelectedSpecOptionChoices(a.twoSpecId)[0] && getSelectedSpecOptionChoices(a.twoSpecId)[0].id) }}-->
<!--            {{ typeof (a.specOptionsList[0] && a.specOptionsList[0]) }}-->
<!--            {{a.specOptionsList}}-->

            <a-form-model-item :label="$t('views.goods.form.assocOptions')">
              <a-select
                v-model="a.specOptionsList"
                mode="multiple"
                show-search
                allow-clear
                :placeholder="$t('views.goods.form.assocOptionsPlaceholder')"
                :filter-option="filterAllSpecOption"
                :getPopupContainer="trigger => trigger.parentNode"
                @change="() => onAssociationTwoSpecOptionsChange(a)"
              >
                <a-select-option
                  v-for="opt in getSelectedSpecOptionChoices(a.twoSpecId)"
                  :key="String(opt.id)"
                  :value="String(opt.id)"
                >
                  {{ opt.name }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="8">
            <a-form-model-item :label="$t('views.goods.form.assocDefault')">
              <a-select
                v-model="a.defaultOption"
                show-search
                allow-clear
                :placeholder="$t('views.goods.form.optional')"
                :filter-option="filterAllSpecOption"
                :getPopupContainer="trigger => trigger.parentNode"
              >
                <a-select-option
                  v-for="oid in (Array.isArray(a.specOptionsList) ? a.specOptionsList : [])"
                  :key="String(oid)"
                  :value="String(oid)"
                >
                  {{ getSpecOptionNameById(oid) || optionLabelFallback(oid) }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-card>

      <a-form-model-item :label="$t('views.goods.form.compositeMaterials')" prop="compositeMaterials">
        <a-select
          v-model="compositeMaterialsSelected"
          mode="multiple"
          show-search
          allow-clear
          :filter-option="filterCategoryOption"
          :loading="calculationSpecOptionLoading"
          :placeholder="$t('views.goods.form.compositeMaterialsPlaceholder')"
          @focus="onCalculationSpecOptionFocus"
          @change="onCompositeMaterialsSelectedChange"
        >
          <a-select-option
            v-for="item in calculationSpecOptionOptions"
            :key="'cm-' + String(item.id)"
            :value="String(item.id)"
          >
            {{ getCalcSpecOptionLabel(item) }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.compositeCost')" prop="compositeCost">
        <a-input-number
          v-model="form.compositeCost"
          :min="0"
          :precision="2"
          :step="0.01"
          allow-clear
          style="width: 100%;"
          :placeholder="$t('views.goods.form.compositeCostPlaceholder')"
        />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.passRate')" prop="passRate">
        <a-input-number
          v-model="form.passRate"
          :min="0"
          :precision="2"
          :step="0.01"
          allow-clear
          style="width: 100%;"
          :placeholder="$t('views.goods.form.passRatePlaceholder')"
        />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.toolIds')" prop="toolIds">
        <a-select
          v-model="toolIdsSelected"
          mode="multiple"
          show-search
          allow-clear
          :filter-option="false"
          :loading="cuttingToolLoading"
          :placeholder="$t('views.goods.form.toolIdsPlaceholder')"
          @search="onCuttingToolSearch"
          @focus="onCuttingToolFocus"
          @change="onToolIdsSelectedChange"
        >
          <a-select-option
            v-for="item in cuttingToolOptions"
            :key="String(item.id)"
            :value="String(item.id)"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.machineTuning')" prop="machineTuning">
        <a-select
          v-model="machineTuningSelected"
          mode="multiple"
          show-search
          allow-clear
          :filter-option="filterCategoryOption"
          :loading="calculationSpecOptionLoading"
          :placeholder="$t('views.goods.form.machineTuningPlaceholder')"
          @focus="onCalculationSpecOptionFocus"
          @change="onMachineTuningSelectedChange"
        >
          <a-select-option
            v-for="item in calculationSpecOptionOptions"
            :key="'mt-' + String(item.id)"
            :value="String(item.id)"
          >
            {{ getCalcSpecOptionLabel(item) }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.machineCost')" prop="machineCost">
        <a-input-number
          v-model="form.machineCost"
          :min="0"
          :precision="2"
          :step="0.01"
          allow-clear
          style="width: 100%;"
          :placeholder="$t('views.goods.form.machineCostPlaceholder')"
        />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.machineId')" prop="machineId">
        <a-select
          v-model="form.machineId"
          show-search
          allow-clear
          :filter-option="false"
          :loading="machineLoading"
          :placeholder="$t('views.goods.form.machineIdPlaceholder')"
          @search="onMachineSearch"
          @focus="onMachineFocus"
        >
          <a-select-option
            v-for="item in machineOptions"
            :key="String(item.id)"
            :value="item.id"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
<!--      <a-form-model-item :label="$t('views.goods.form.printMultiples')" prop="printMultiplesId">-->
<!--        <a-select-->
<!--          v-model="form.printMultiplesId"-->
<!--          show-search-->
<!--          allow-clear-->
<!--          :loading="printMultiplesLoading"-->
<!--          :placeholder="$t('views.goods.form.printMultiplesPlaceholder')"-->
<!--          :filter-option="filterPrintMultiplesOption"-->
<!--          @focus="onPrintMultiplesFocus"-->
<!--        >-->
<!--          <a-select-option-->
<!--            v-for="item in printMultiplesOptions"-->
<!--            :key="'pm-' + String(item.id)"-->
<!--            :value="item.id"-->
<!--          >-->
<!--            {{ item.name }}-->
<!--          </a-select-option>-->
<!--        </a-select>-->
<!--      </a-form-model-item>-->
      <a-form-model-item :label="$t('views.goods.form.specialMultipleIds')" prop="specialMultipleIds">
        <a-select
          v-model="specialMultipleIdsSelected"
          mode="multiple"
          show-search
          allow-clear
          :filter-option="filterSpecialMultipleOption"
          :loading="specialMultipleLoading"
          :placeholder="$t('views.goods.form.specialMultipleIdsPlaceholder')"
          option-filter-prop="_label"
          option-label-prop="_label"
          style="width: 100%"
          :getPopupContainer="trigger => trigger.parentNode"
          @focus="onSpecialMultipleFocus"
          @change="onSpecialMultipleIdsSelectedChange"
        >
          <a-select-option
            v-for="item in specialMultipleOptions"
            :key="'sm-' + String(item.id)"
            :value="String(item.id)"
            :_label="getSpecialMultipleOptionLabel(item)"
          >
            {{ getSpecialMultipleOptionLabel(item) }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.colorAdjustment')" prop="colorAdjustment">
        <a-input-number
          v-model="form.colorAdjustment"
          :min="0"
          :precision="2"
          :step="0.01"
          allow-clear
          style="width: 100%;"
          :placeholder="$t('views.goods.form.colorAdjustmentPlaceholder')"
        />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.packagingFee')" prop="packagingFee">
        <a-input-number
          v-model="form.packagingFee"
          :min="0"
          :precision="2"
          :step="0.01"
          allow-clear
          style="width: 100%;"
          :placeholder="$t('views.goods.form.packagingFeePlaceholder')"
        />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.sealStripRemaining')" prop="sealStripRemaining">
        <a-input-number
          v-model="form.sealStripRemaining"
          :min="0"
          :precision="0"
          :step="1"
          style="width: 100%;"
          :placeholder="$t('views.goods.form.sealStripRemainingPlaceholder')"
        />
      </a-form-model-item>
      <!-- installationFee 已注释
      <a-form-model-item :label="$t('views.goods.form.installationFee')" prop="installationFee">
        <a-input-number
          v-model="form.installationFee"
          :min="0"
          :precision="2"
          :step="0.01"
          allow-clear
          style="width: 100%;"
          :placeholder="$t('views.goods.form.installationFeePlaceholder')"
        />
      </a-form-model-item>
      -->
      <a-form-model-item :label="$t('views.goods.form.url')" prop="url" >
        <file-upload v-model="form.url" type="image" @change="changeIcon"></file-upload>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.description')" prop="description" >
        <a-input v-model="form.description" :placeholder="$t('views.goods.form.descriptionPlaceholder')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.descriptionEn')" prop="descriptionEn" >
        <a-input v-model="form.descriptionEn" type="textarea" :auto-size="{ minRows: 2, maxRows: 6 }" :placeholder="$t('views.goods.form.descriptionEnPlaceholder')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.content')" prop="content" >
        <editor v-if="open" :key="editorKey" v-model="form.content" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.goods.form.contentEn')" prop="contentEn" >
        <editor v-if="open" :key="String(editorKey) + '-en'" v-model="form.contentEn" />
      </a-form-model-item>
      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            {{ $t('views.goods.form.save') }}
          </a-button>
          <a-button type="dashed" @click="cancel">
            {{ $t('views.goods.form.cancel') }}
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>
import { getGoods, addGoods, updateGoods } from '@/api/user/goods'
import { listCategory } from '@/api/user/category'
import { listSpec } from '@/api/user/spec'
import { listSpecOption, listSpecOptionJoinSpecCalculation } from '@/api/user/specOption'
import { listMachine } from '@/api/user/machine'
import { listPrintMultiples } from '@/api/user/printMultiples'
import { listSpecialMultiple } from '@/api/user/specialMultiple'
import { listCuttingTool } from '@/api/user/cuttingTool'
import Editor from '@/components/Editor'

export default {
  name: 'CreateForm',
  props: {
  },
  components: {
    Editor
  },
  data () {
    return {
      submitLoading: false,
      formTitle: '',
      categoryLoading: false,
      categoryOptions: [],
      specLoading: false,
      specOptions: [],
      machineLoading: false,
      machineOptions: [],
      _machineSearchTimer: null,
      printMultiplesLoading: false,
      printMultiplesOptions: [],
      specialMultipleLoading: false,
      specialMultipleOptions: [],
      specialMultipleIdsSelected: [],
      cuttingToolLoading: false,
      cuttingToolOptions: [],
      _cuttingToolSearchTimer: null,
      /** 刀具费用多选（与 form.toolIds 逗号串同步） */
      toolIdsSelected: [],
      /** 参与计算的规格选项（list-join-spec-calculation），供复合材料/调机成本多选共用 */
      calculationSpecOptionLoading: false,
      calculationSpecOptionOptions: [],
      compositeMaterialsSelected: [],
      machineTuningSelected: [],
      specOptionLoadingMap: {},
      specOptionMap: {},
      specSelectedOptionMap: {},
      specSortMap: {},
      specOptionCombinationMap: {},
      specOptionCombinationSelectMap: {},
      /** 组合行 name/nameEn 草稿（与行对象解耦，避免 Vue2/Drawer 下响应式丢失导致提交无值） */
      combinationFieldDraft: {},
      /** 为无 id 的新组合行分配稳定 cid，用于草稿键 */
      _combDraftSeq: 0,
      specOptionTypeMap: {},
      specOptionSortMap: {},
      allSpecOptionLoading: false,
      allSpecOptionOptions: [],
      editorKey: 0,
      associationList: [],
      // 表单参数
      form: {
        id: null,

        categoryId: null,

        /** 商品类型：0-自立袋 1-三边封 */
        type: 0,

        name: null,

        nameEn: null,

        machineId: null,

        /** 印刷倍数ID，关联 zb_print_multiples.id */
        printMultiplesId: null,

        /** 特殊倍数 zb_special_multiple.id 多个，逗号拼接 */
        specialMultipleIds: null,

        /** 刀具费用 id 多个，逗号拼接 */
        toolIds: null,

        /** 颜色调整费（decimal，两位小数） */
        colorAdjustment: null,

        /** 耐介质包装费（decimal，两位小数） */
        packagingFee: null,

        /** 封口条余量(mm)，整数，默认 0 */
        sealStripRemaining: 0,

        /** 单条封口条安装费（decimal，两位小数），默认 0 */
        // installationFee: 0,

        /** 复合材料合格率（decimal，两位小数） */
        passRate: null,

        /** 复合材料其他成本：规格选项 id 多个，逗号拼接 */
        compositeMaterials: null,

        /** 调机成本其他费用：规格选项 id 多个，逗号拼接 */
        machineTuning: null,

        /** 复合成本(复合材料) 元/米/层（decimal，两位小数） */
        compositeCost: null,

        /** 复合成本(调机成本) 元/米/层（decimal，两位小数） */
        machineCost: null,

        specIdList: [],
        specOptionSelectList: [],

        url: null,

        description: null,

        descriptionEn: null,

        content: null,

        contentEn: null,

        sort: null,

        status: 1,

        createTime: null,

        remark: null,

      },
      // 1增加,2修改
      formType: 1,
      open: false
    }
  },
  filters: {
  },
  created () {
  },
  computed: {
    rules () {
      return {
        categoryId: [
          { required: true, message: this.$t('views.goods.rule.categoryRequired'), trigger: 'change' }
        ],
        type: [
          { required: true, message: this.$t('views.goods.rule.typeRequired'), trigger: 'change' }
        ],
        name: [
          { required: true, message: this.$t('views.goods.rule.nameRequired'), trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    '$i18n.locale' () {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    'form.specIdList': {
      handler (val) {
        this.onSpecIdListChange(val)
      },
      deep: true
    }
  },
  mounted () {
  },
  methods: {
    onMachineFocus () {
      if (Array.isArray(this.machineOptions) && this.machineOptions.length) return
      this.loadMachineOptions('')
    },
    onMachineSearch (value) {
      const keyword = value !== undefined && value !== null ? String(value) : ''
      if (this._machineSearchTimer) {
        clearTimeout(this._machineSearchTimer)
      }
      this._machineSearchTimer = setTimeout(() => {
        this.loadMachineOptions(keyword)
      }, 250)
    },
    loadMachineOptions (keyword) {
      const name = keyword !== undefined && keyword !== null ? String(keyword).trim() : ''
      this.machineLoading = true
      return listMachine({ name }).then(res => {
        const list = (res && res.data) ? res.data : []
        this.machineOptions = Array.isArray(list) ? list : []
      }).catch(() => {
        this.machineOptions = []
      }).finally(() => {
        this.machineLoading = false
      })
    },
    onCuttingToolFocus () {
      if (Array.isArray(this.cuttingToolOptions) && this.cuttingToolOptions.length) return
      this.loadCuttingToolOptions('')
    },
    onCuttingToolSearch (value) {
      const keyword = value !== undefined && value !== null ? String(value) : ''
      if (this._cuttingToolSearchTimer) {
        clearTimeout(this._cuttingToolSearchTimer)
      }
      this._cuttingToolSearchTimer = setTimeout(() => {
        this.loadCuttingToolOptions(keyword)
      }, 250)
    },
    loadCuttingToolOptions (keyword) {
      const name = keyword !== undefined && keyword !== null ? String(keyword).trim() : ''
      this.cuttingToolLoading = true
      return listCuttingTool({ name }).then(res => {
        const list = (res && res.data) ? res.data : []
        this.cuttingToolOptions = Array.isArray(list) ? list : []
      }).catch(() => {
        this.cuttingToolOptions = []
      }).finally(() => {
        this.cuttingToolLoading = false
      })
    },
    parseToolIdsStringToArray (toolIds) {
      if (toolIds === undefined || toolIds === null || toolIds === '') return []
      const s = String(toolIds).trim()
      if (!s) return []
      return s.split(',').map(x => String(x).trim()).filter(Boolean)
    },
    syncFormToolIdsFromSelected () {
      const arr = Array.isArray(this.toolIdsSelected) ? this.toolIdsSelected : []
      const clean = arr.map(id => String(id).trim()).filter(Boolean)
      this.form.toolIds = clean.length ? clean.join(',') : null
    },
    onToolIdsSelectedChange () {
      this.syncFormToolIdsFromSelected()
    },
    onCalculationSpecOptionFocus () {
      if (Array.isArray(this.calculationSpecOptionOptions) && this.calculationSpecOptionOptions.length) return
      this.loadCalculationSpecOptions()
    },
    loadCalculationSpecOptions () {
      if (Array.isArray(this.calculationSpecOptionOptions) && this.calculationSpecOptionOptions.length) {
        return Promise.resolve()
      }
      this.calculationSpecOptionLoading = true
      return listSpecOptionJoinSpecCalculation().then(res => {
        const list = (res && res.data) ? res.data : []
        this.calculationSpecOptionOptions = Array.isArray(list) ? list : []
      }).catch(() => {
        this.calculationSpecOptionOptions = []
      }).finally(() => {
        this.calculationSpecOptionLoading = false
      })
    },
    getCalcSpecOptionLabel (item) {
      if (!item) return ''
      const n = item.name || ''
      const sid = item.specId
      const specName = this.getSpecName(sid) || (sid != null && sid !== '' ? this.specLabelFallback(sid) : '')
      return specName ? `${specName} / ${n}` : n
    },
    specLabelFallback (specId) {
      return this.$t('views.goods.form.specFallback', { id: specId })
    },
    optionLabelFallback (oid) {
      return this.$t('views.goods.form.optionFallback', { id: oid })
    },
    syncFormCompositeMaterialsFromSelected () {
      const arr = Array.isArray(this.compositeMaterialsSelected) ? this.compositeMaterialsSelected : []
      const clean = arr.map(id => String(id).trim()).filter(Boolean)
      this.form.compositeMaterials = clean.length ? clean.join(',') : null
    },
    syncFormMachineTuningFromSelected () {
      const arr = Array.isArray(this.machineTuningSelected) ? this.machineTuningSelected : []
      const clean = arr.map(id => String(id).trim()).filter(Boolean)
      this.form.machineTuning = clean.length ? clean.join(',') : null
    },
    onCompositeMaterialsSelectedChange () {
      this.syncFormCompositeMaterialsFromSelected()
    },
    onMachineTuningSelectedChange () {
      this.syncFormMachineTuningFromSelected()
    },
    /** 详情/接口数值转为两位小数的 number，空为 null（供 a-input-number） */
    parseGoodsDecimal2 (raw) {
      if (raw === undefined || raw === null || raw === '') return null
      const n = typeof raw === 'number' ? raw : parseFloat(String(raw).trim().replace(/,/g, ''))
      if (Number.isNaN(n)) return null
      return Math.round(n * 100) / 100
    },
    /** 封口条余量(mm)：非负整数，空/非法按 0 */
    normalizeSealStripRemaining (raw) {
      if (raw === undefined || raw === null || raw === '') return 0
      const n = parseInt(String(raw).trim(), 10)
      if (Number.isNaN(n) || n < 0) return 0
      return n
    },
    getSortedSpecOptions (specId) {
      const k = String(specId)
      const list = Array.isArray(this.specOptionMap[k]) ? this.specOptionMap[k] : []
      const sortMap = (this.specOptionSortMap && this.specOptionSortMap[k]) ? this.specOptionSortMap[k] : {}
      return [...list].sort((a, b) => {
        const sa = Object.prototype.hasOwnProperty.call(sortMap, String(a && a.id)) ? Number(sortMap[String(a.id)]) : 0
        const sb = Object.prototype.hasOwnProperty.call(sortMap, String(b && b.id)) ? Number(sortMap[String(b.id)]) : 0
        if (sa !== sb) return sa - sb
        const oa = a && a.sort !== undefined && a.sort !== null ? Number(a.sort) : 0
        const ob = b && b.sort !== undefined && b.sort !== null ? Number(b.sort) : 0
        if (oa !== ob) return oa - ob
        return String(a && a.id ? a.id : '').localeCompare(String(b && b.id ? b.id : ''))
      })
    },
    isSpecOptionSelected (specId, optId) {
      const k = String(specId)
      const list = Array.isArray(this.specSelectedOptionMap[k]) ? this.specSelectedOptionMap[k] : []
      return list.includes(optId)
    },
    toggleSpecOption (specId, optId) {
      const k = String(specId)
      const list = Array.isArray(this.specSelectedOptionMap[k]) ? [...this.specSelectedOptionMap[k]] : []
      const idx = list.indexOf(optId)
      if (idx >= 0) {
        list.splice(idx, 1)
      } else {
        list.push(optId)
      }
      this.$set(this.specSelectedOptionMap, k, list)
      this.pruneAssociationList()
    },
    getGroupRowKey (row, ridx) {
      if (row && row.id) return `id_${String(row.id)}`
      return `tmp_${String(ridx)}`
    },
    isDefaultMulti (specId, optionId) {
      const k = String(specId)
      const oid = String(optionId)
      const m = (this.specOptionTypeMap && this.specOptionTypeMap[k]) ? this.specOptionTypeMap[k] : {}
      return (m && (m[oid] === 1 || m[oid] === '1'))
    },
    getDefaultGroupModel (specId, optionId) {
      const rows = this.getCombinationRows(specId, optionId)
      if (!Array.isArray(rows) || rows.length === 0) return this.isDefaultMulti(specId, optionId) ? [] : undefined
      const out = []
      rows.forEach((r, idx) => {
        if (r && r.isDefault) {
          out.push(this.getGroupRowKey(r, idx))
        }
      })
      if (this.isDefaultMulti(specId, optionId)) {
        return out
      }
      return out.length ? out[0] : undefined
    },
    onDefaultGroupModelChange (specId, optionId, v) {
      const k = String(specId)
      const oid = String(optionId)
      const arr = this.isDefaultMulti(specId, optionId) ? (Array.isArray(v) ? v : []) : (v ? [v] : [])
      const curSpec = this.specOptionCombinationSelectMap[k] || {}
      const rows = Array.isArray(curSpec[oid]) ? curSpec[oid].slice() : []
      const nextRows = rows.map((r, idx) => {
        const key = this.getGroupRowKey(r, idx)
        return { ...(r || {}), isDefault: arr.includes(key) }
      })
      const nextSpec = { ...(curSpec || {}) }
      nextSpec[oid] = nextRows
      this.$set(this.specOptionCombinationSelectMap, k, nextSpec)
    },
    changeIcon (e) {
      this.form.url = e
    },
    filterCategoryOption (input, option) {
      const text = option && option.componentOptions && option.componentOptions.children && option.componentOptions.children[0] && option.componentOptions.children[0].text
      return (text || '').toLowerCase().includes((input || '').toLowerCase())
    },
    loadCategoryOptions () {
      if (this.categoryLoading) return
      this.categoryLoading = true
      listCategory({}).then(res => {
        const data = (res && res.data) || []
        this.categoryOptions = Array.isArray(data) ? data : []
      }).finally(() => {
        this.categoryLoading = false
      })
    },
    filterPrintMultiplesOption (input, option) {
      const text = option && option.componentOptions && option.componentOptions.children && option.componentOptions.children[0] && option.componentOptions.children[0].text
      return (text || '').toLowerCase().includes((input || '').toLowerCase())
    },
    loadPrintMultiplesOptions () {
      if (this.printMultiplesLoading) return
      this.printMultiplesLoading = true
      listPrintMultiples({}).then(res => {
        const raw = (res && res.data) != null ? res.data : []
        const data = Array.isArray(raw) ? raw : (raw.records || raw.list || [])
        this.printMultiplesOptions = Array.isArray(data) ? data : []
      }).catch(() => {
        this.printMultiplesOptions = []
      }).finally(() => {
        this.printMultiplesLoading = false
      })
    },
    onPrintMultiplesFocus () {
      if (!Array.isArray(this.printMultiplesOptions) || !this.printMultiplesOptions.length) {
        this.loadPrintMultiplesOptions()
      }
    },
    loadSpecialMultipleOptions () {
      if (this.specialMultipleLoading) return
      this.specialMultipleLoading = true
      listSpecialMultiple({}).then(res => {
        const raw = (res && res.data) != null ? res.data : []
        const data = Array.isArray(raw) ? raw : (raw.records || raw.list || [])
        this.specialMultipleOptions = Array.isArray(data) ? data : []
      }).catch(() => {
        this.specialMultipleOptions = []
      }).finally(() => {
        this.specialMultipleLoading = false
      })
    },
    onSpecialMultipleFocus () {
      if (!Array.isArray(this.specialMultipleOptions) || !this.specialMultipleOptions.length) {
        this.loadSpecialMultipleOptions()
      }
    },
    filterSpecialMultipleOption (input, option) {
      const text =
        option &&
        option.componentOptions &&
        option.componentOptions.children &&
        option.componentOptions.children[0] &&
        option.componentOptions.children[0].text
      return (text || '').toLowerCase().includes((input || '').toLowerCase())
    },
    getSpecialMultipleOptionLabel (item) {
      if (!item) return ''
      const idStr = item.id != null && item.id !== '' ? String(item.id) : ''
      const name = item.productType || item.name || ''
      const t = item.type
      let inner = ''
      if (t === undefined || t === null || t === '') {
        inner = name
      } else {
        const typeTxt = this.$t('views.specialMultiple.type.' + t)
        inner = name ? `${name} · ${typeTxt}` : typeTxt
      }
      if (idStr) {
        return inner ? `${idStr} · ${inner}` : idStr
      }
      return inner
    },
    syncFormSpecialMultipleIdsFromSelected () {
      const arr = Array.isArray(this.specialMultipleIdsSelected) ? this.specialMultipleIdsSelected : []
      const clean = arr.map(id => String(id).trim()).filter(Boolean)
      this.form.specialMultipleIds = clean.length ? clean.join(',') : null
    },
    onSpecialMultipleIdsSelectedChange () {
      this.syncFormSpecialMultipleIdsFromSelected()
    },
    filterSpecOption (input, option) {
      const text = option && option.componentOptions && option.componentOptions.children && option.componentOptions.children[0] && option.componentOptions.children[0].text
      return (text || '').toLowerCase().includes((input || '').toLowerCase())
    },
    loadSpecOptions () {
      if (this.specLoading) return
      this.specLoading = true
      listSpec({}).then(res => {
        const data = (res && res.data) || []
        this.specOptions = Array.isArray(data) ? data : []
      }).finally(() => {
        this.specLoading = false
      })
    },
    filterAllSpecOption (input, option) {
      const text = option && option.componentOptions && option.componentOptions.children && option.componentOptions.children[0] && option.componentOptions.children[0].text
      return (text || '').toLowerCase().includes((input || '').toLowerCase())
    },
    loadAllSpecOptionOptions () {
      if (this.allSpecOptionLoading) return
      if (Array.isArray(this.allSpecOptionOptions) && this.allSpecOptionOptions.length) return
      this.allSpecOptionLoading = true
      listSpecOption({}).then(res => {
        const data = (res && res.data) || []
        this.allSpecOptionOptions = Array.isArray(data) ? data : []
      }).finally(() => {
        this.allSpecOptionLoading = false
      })
    },
    getSpecOptionNameById (id) {
      const hit = (this.allSpecOptionOptions || []).find(e => e && e.id == id)
      return hit && hit.name
    },
    addAssociationRow () {
      if (!Array.isArray(this.associationList)) {
        this.associationList = []
      }
      this.associationList.unshift({
        id: null,
        goodId: this.form && this.form.id ? this.form.id : null,
        specId: null,
        specOptionId: null,
        twoSpecId: null,
        specOptionsList: [],
        defaultOption: null,
        sort: 0,
        status: 1,
        _rowKey: `assoc-${Date.now()}-${Math.random().toString(16).slice(2)}`
      })
      this.loadAllSpecOptionOptions()
    },
    removeAssociationRow (idx) {
      if (!Array.isArray(this.associationList)) return
      this.associationList.splice(idx, 1)
    },
    getAssociationTwoSpecChoices (specId) {
      const ids = Array.isArray(this.form.specIdList) ? this.form.specIdList.filter(Boolean) : []
      const sid = specId ? String(specId) : null
      return ids.filter(x => String(x) !== sid)
    },
    getSelectedSpecOptionChoices (specId) {
      if (!specId) return []
      const k = String(specId)
      const all = Array.isArray(this.specOptionMap[k]) ? this.specOptionMap[k] : []
      const selected = this.getSpecSelectedOptionIds(specId)
      if (!Object.prototype.hasOwnProperty.call(this.specSelectedOptionMap, k)) {
        return all
      }
      return all.filter(o => o && selected.includes(o.id))
    },
    getSpecSelectedOptionIds (specId) {
      if (!specId) return []
      const k = String(specId)
      if (!Object.prototype.hasOwnProperty.call(this.specSelectedOptionMap, k)) {
        return []
      }
      return Array.isArray(this.specSelectedOptionMap[k]) ? this.specSelectedOptionMap[k].filter(Boolean) : []
    },
    /** 规格选项变更后，移除或修正已失效的关联设置 */
    pruneAssociationList () {
      if (!Array.isArray(this.associationList)) {
        this.associationList = []
        return
      }
      const specIdSet = new Set((Array.isArray(this.form.specIdList) ? this.form.specIdList : []).map(x => String(x)))
      this.associationList = this.associationList.filter(row => {
        if (!row) return false
        if (!row.specId && !row.twoSpecId && !row.specOptionId) {
          return true
        }
        if (!row.specId || !row.specOptionId || !row.twoSpecId) {
          return false
        }
        if (!specIdSet.has(String(row.specId)) || !specIdSet.has(String(row.twoSpecId))) {
          return false
        }
        if (String(row.specId) === String(row.twoSpecId)) {
          return false
        }
        const specSelected = this.getSpecSelectedOptionIds(row.specId).map(String)
        const twoSelected = this.getSpecSelectedOptionIds(row.twoSpecId).map(String)
        if (!specSelected.length || !twoSelected.length) {
          return false
        }
        if (!specSelected.includes(String(row.specOptionId))) {
          return false
        }
        const list = Array.isArray(row.specOptionsList) ? row.specOptionsList.map(x => String(x)).filter(Boolean) : []
        const filtered = list.filter(id => twoSelected.includes(id))
        row.specOptionsList = filtered
        if (row.defaultOption) {
          const def = String(row.defaultOption)
          if (!filtered.includes(def)) {
            row.defaultOption = null
          }
        }
        return filtered.length > 0
      })
    },
    onAssociationSpecChange (row) {
      if (!row) return
      row.specOptionId = null
      if (row.twoSpecId && String(row.twoSpecId) === String(row.specId)) {
        row.twoSpecId = null
      }
    },
    onAssociationSpecOptionChange (row) {
      if (!row) return
    },
    onAssociationTwoSpecChange (row) {
      if (!row) return
      row.specOptionsList = []
      row.defaultOption = null
    },
    onAssociationTwoSpecOptionsChange (row) {
      if (!row) return
      const list = Array.isArray(row.specOptionsList) ? row.specOptionsList.map(x => String(x)).filter(Boolean) : []
      row.specOptionsList = list

      if (row.defaultOption !== undefined && row.defaultOption !== null && row.defaultOption !== '') {
        row.defaultOption = String(row.defaultOption)
      } else {
        row.defaultOption = null
      }
      // 默认值不在列表内：清空
      if (row.defaultOption && list.length && !list.includes(row.defaultOption)) {
        row.defaultOption = null
      }
    },
    getSpecName (specId) {
      const sid = specId
      const hit = (this.specOptions || []).find(e => e && e.id === sid)
      return hit && hit.name
    },
    getSpecIsCalculation (specId) {
      const sid = specId
      const hit = (this.specOptions || []).find(e => e && e.id === sid)
      if (!hit) return 0
      const v = hit.isCalculation
      if (v === 1 || v === '1') return 1
      return 0
    },
    getSpecIsCustomization (specId) {
      const sid = specId
      const hit = (this.specOptions || []).find(e => e && e.id === sid)
      if (!hit) return 0
      const v = hit.isCustomization
      if (v === 1 || v === '1') return 1
      return 0
    },
    getSpecIdentityRestrictions (specId) {
      const sid = specId
      const hit = (this.specOptions || []).find(e => e && e.id === sid)
      if (!hit) return 0
      const v = hit.identityRestrictions
      if (v === 1 || v === '1') return 1
      return 0
    },
    onSpecIdListChange (specIdList) {
      const list = Array.isArray(specIdList) ? specIdList.filter(Boolean) : []
      const keyList = list.map(v => String(v))

      // 清理被移除的规格缓存
      Object.keys(this.specOptionMap || {}).forEach(k => {
        if (!keyList.includes(k)) {
          this.$delete(this.specOptionMap, k)
        }
      })
      Object.keys(this.specSelectedOptionMap || {}).forEach(k => {
        if (!keyList.includes(k)) {
          this.$delete(this.specSelectedOptionMap, k)
        }
      })
      Object.keys(this.specOptionLoadingMap || {}).forEach(k => {
        if (!keyList.includes(k)) {
          this.$delete(this.specOptionLoadingMap, k)
        }
      })
      Object.keys(this.specSortMap || {}).forEach(k => {
        if (!keyList.includes(k)) {
          this.$delete(this.specSortMap, k)
        }
      })
      Object.keys(this.specOptionCombinationMap || {}).forEach(k => {
        if (!keyList.includes(k)) {
          this.$delete(this.specOptionCombinationMap, k)
        }
      })
      Object.keys(this.specOptionCombinationSelectMap || {}).forEach(k => {
        if (!keyList.includes(k)) {
          this.$delete(this.specOptionCombinationSelectMap, k)
        }
      })
      Object.keys(this.specOptionSortMap || {}).forEach(k => {
        if (!keyList.includes(k)) {
          this.$delete(this.specOptionSortMap, k)
        }
      })

      // 逐个加载规格选项
      list.forEach(specId => {
        const k = String(specId)
        if (this.specSortMap[k] === undefined) {
          this.$set(this.specSortMap, k, 0)
        }
        if (this.specOptionCombinationMap[k] === undefined) {
          this.$set(this.specOptionCombinationMap, k, {})
        }
        if (this.specOptionCombinationSelectMap[k] === undefined) {
          this.$set(this.specOptionCombinationSelectMap, k, {})
        }
      if (this.specOptionTypeMap[k] === undefined) {
        this.$set(this.specOptionTypeMap, k, {})
      }
        if (this.specOptionSortMap[k] === undefined) {
          this.$set(this.specOptionSortMap, k, {})
        }
        if (Array.isArray(this.specOptionMap[k])) {
          return
        }
        this.loadSpecOptionBySpecId(specId)
      })
      this.pruneAssociationList()
    },
    loadSpecOptionBySpecId (specId) {
      const k = String(specId)
      if (this.specOptionLoadingMap[k]) return
      this.$set(this.specOptionLoadingMap, k, true)
      listSpecOption({ specId: specId, parentId: 0 }).then(res => {
        const data = (res && res.data) || []
        const list = Array.isArray(data) ? data : []
        this.$set(this.specOptionMap, k, list)

        // 组合开关：默认否（如果没有回显数据则置空map）
        if (!this.specOptionCombinationMap[k] || typeof this.specOptionCombinationMap[k] !== 'object') {
          this.$set(this.specOptionCombinationMap, k, {})
        }
        if (!this.specOptionCombinationSelectMap[k] || typeof this.specOptionCombinationSelectMap[k] !== 'object') {
          this.$set(this.specOptionCombinationSelectMap, k, {})
        }
        if (!this.specOptionTypeMap[k] || typeof this.specOptionTypeMap[k] !== 'object') {
          this.$set(this.specOptionTypeMap, k, {})
        }
        if (!this.specOptionSortMap[k] || typeof this.specOptionSortMap[k] !== 'object') {
          this.$set(this.specOptionSortMap, k, {})
        }

        // 默认全选：仅在该规格尚未初始化选中值时全选（已保存的空选择需保持为空）
        if (!Object.prototype.hasOwnProperty.call(this.specSelectedOptionMap, k)) {
          const allIds = list.map(e => e && e.id).filter(Boolean)
          this.$set(this.specSelectedOptionMap, k, allIds)
        } else {
          // 过滤掉不存在的选项
          const allIds = list.map(e => e && e.id).filter(Boolean)
          const keep = (this.specSelectedOptionMap[k] || []).filter(id => allIds.includes(id))
          if (keep.length !== (this.specSelectedOptionMap[k] || []).length) {
            this.$set(this.specSelectedOptionMap, k, keep)
          }
        }

        // 如果某些组合开关对应的选项已不存在，清理掉
        const allIdStrSet = new Set(list.map(e => e && String(e.id)).filter(Boolean))
        const combMap = this.specOptionCombinationMap[k] || {}
        const nextComb = {}
        Object.keys(combMap).forEach(idStr => {
          if (allIdStrSet.has(idStr) && combMap[idStr]) {
            nextComb[idStr] = true
          }
        })
        this.$set(this.specOptionCombinationMap, k, nextComb)

        // 组合类型：默认 0，清理已不存在 optionId/未开启组合的项
        const typeMap = this.specOptionTypeMap[k] || {}
        const nextType = {}
        Object.keys(typeMap).forEach(oid => {
          if (!allIdStrSet.has(oid)) return
          if (!nextComb[oid]) return
          const v = typeMap[oid]
          nextType[oid] = (v === 1 || v === '1') ? 1 : 0
        })
        // 对所有开启组合但没有设置的，补默认 0
        Object.keys(nextComb).forEach(oid => {
          if (nextType[oid] === undefined || nextType[oid] === null) {
            nextType[oid] = 0
          }
        })
        this.$set(this.specOptionTypeMap, k, nextType)

        // 组合选择：清理掉已不存在的 optionId，并且组合开关关闭时清空选择
        const selectMap = this.specOptionCombinationSelectMap[k] || {}
        const nextSelect = {}
        Object.keys(selectMap).forEach(oid => {
          if (!allIdStrSet.has(oid)) {
            return
          }
          if (!nextComb[oid]) {
            return
          }
          const v = selectMap[oid]
          if (Array.isArray(v)) {
            // 新结构：[{sort, optionIdList}]
            if (v.length && v[0] && typeof v[0] === 'object' && (Object.prototype.hasOwnProperty.call(v[0], 'optionIdList') || Object.prototype.hasOwnProperty.call(v[0], 'sort'))) {
              const rows = v.map(r => {
                const optionIdList = Array.isArray(r.optionIdList) ? r.optionIdList.filter(Boolean) : []
                const nameEnRaw = r.nameEn != null && r.nameEn !== '' ? r.nameEn : r.name_en
                const nid = this.normalizeGoodsCombinationRowId(r.id)
                const out = { id: nid, name: r.name || null, nameEn: nameEnRaw != null && String(nameEnRaw).trim() !== '' ? String(nameEnRaw) : null, sort: r.sort !== undefined && r.sort !== null ? r.sort : 0, optionIdList, isDefault: !!r.isDefault }
                // 异步重建行时必须保留客户端 id，否则 combinationFieldDraft 的 cid_* 与 pick 时用的 key 脱节，nameEn 提交丢失
                if (r && r._clientCid != null && !this.hasCombinationRowServerId(out)) {
                  out._clientCid = r._clientCid
                }
                return out
              }).filter(r => Array.isArray(r.optionIdList) && r.optionIdList.length)
              nextSelect[oid] = rows
              return
            }
            // 旧结构：[[1,2],[3,4]]
            if (v.length && Array.isArray(v[0])) {
            nextSelect[oid] = v
                .map(arr => (Array.isArray(arr) ? arr.filter(Boolean) : []))
                .filter(arr => arr.length)
                .map(arr => this.emptyCombinationRow({ optionIdList: arr, sort: 0, isDefault: false }))
              return
            }
            // 旧结构：[1,2]
            const one = v.filter(Boolean)
            nextSelect[oid] = one.length ? [this.emptyCombinationRow({ optionIdList: one, sort: 0, isDefault: false })] : []
          } else {
            nextSelect[oid] = []
          }
        })
        this.$set(this.specOptionCombinationSelectMap, k, nextSelect)

        // 选项排序：默认 0，清理已不存在 optionId
        const sortMap = this.specOptionSortMap[k] || {}
        const nextSort = { ...sortMap }
        list.forEach(o => {
          if (!o || !o.id) return
          const oid = String(o.id)
          if (nextSort[oid] === undefined || nextSort[oid] === null || nextSort[oid] === '') {
            nextSort[oid] = 0
          }
        })
        Object.keys(nextSort).forEach(oid => {
          if (!allIdStrSet.has(oid)) {
            delete nextSort[oid]
          }
        })
        this.$set(this.specOptionSortMap, k, nextSort)
      }).finally(() => {
        this.$set(this.specOptionLoadingMap, k, false)
      })
    },
    selectAllSpecOptions (specId) {
      const k = String(specId)
      const list = Array.isArray(this.specOptionMap[k]) ? this.specOptionMap[k] : []
      const allIds = list.map(e => e && e.id).filter(Boolean)
      this.$set(this.specSelectedOptionMap, k, allIds)
      this.pruneAssociationList()
    },
    clearAllSpecOptions (specId) {
      const k = String(specId)
      this.$set(this.specSelectedOptionMap, k, [])
      this.pruneAssociationList()
    },
    onOptionCombinationChange (specId, optionId, checked) {
      const k = String(specId)
      const oid = String(optionId)
      if (!this.specOptionCombinationMap[k] || typeof this.specOptionCombinationMap[k] !== 'object') {
        this.$set(this.specOptionCombinationMap, k, {})
      }
      if (!this.specOptionCombinationSelectMap[k] || typeof this.specOptionCombinationSelectMap[k] !== 'object') {
        this.$set(this.specOptionCombinationSelectMap, k, {})
      }
      if (!this.specOptionTypeMap[k] || typeof this.specOptionTypeMap[k] !== 'object') {
        this.$set(this.specOptionTypeMap, k, {})
      }
      const next = { ...(this.specOptionCombinationMap[k] || {}) }
      if (checked) {
        next[oid] = true
        this.loadAllSpecOptionOptions()
        // 默认类型：单选(0)
        const typeCur = this.specOptionTypeMap[k] || {}
        if (typeCur[oid] === undefined || typeCur[oid] === null || typeCur[oid] === '') {
          const nextType = { ...(typeCur || {}) }
          nextType[oid] = 0
          this.$set(this.specOptionTypeMap, k, nextType)
        }
        // 默认新增一条空组合项
        const cur = this.specOptionCombinationSelectMap[k] || {}
        if (!Array.isArray(cur[oid]) || cur[oid].length === 0) {
          const nextSelect = { ...(cur || {}) }
          nextSelect[oid] = [this.emptyCombinationRow()]
          this.$set(this.specOptionCombinationSelectMap, k, nextSelect)
        }
      } else {
        delete next[oid]
        const nextSelect = { ...(this.specOptionCombinationSelectMap[k] || {}) }
        delete nextSelect[oid]
        this.$set(this.specOptionCombinationSelectMap, k, nextSelect)
        const nextType = { ...(this.specOptionTypeMap[k] || {}) }
        delete nextType[oid]
        this.$set(this.specOptionTypeMap, k, nextType)
      }
      this.$set(this.specOptionCombinationMap, k, next)
    },

    getCombinationRows (specId, optionId) {
      const k = String(specId)
      const oid = String(optionId)
      const map = this.specOptionCombinationSelectMap[k] || {}
      const rows = map[oid]
      return Array.isArray(rows) ? rows : []
    },
    /** v-for :key，避免行重建后子 Input 复用旧实例导致 nameEn 不更新（调试用：localStorage.setItem('DEBUG_GOODS_COMB','1')） */
    combRowVueKey (specId, optId, row, ridx) {
      const sid = String(specId)
      const oid = String(optId)
      if (row && this.hasCombinationRowServerId(row)) return `gc_${sid}_${oid}_id${String(row.id)}`
      if (row && row._clientCid != null) return `gc_${sid}_${oid}_c${row._clientCid}`
      return `gc_${sid}_${oid}_i${ridx}`
    },
    /** 始终从 map 取当前行，避免 v-for 的 row 与异步刷新后数组项引用不一致（name 正常、nameEn 写丢） */
    getCombRowRef (specId, optId, ridx) {
      const k = String(specId)
      const oid = String(optId)
      const rows = (this.specOptionCombinationSelectMap[k] || {})[oid]
      if (!Array.isArray(rows)) return null
      return rows[ridx] || null
    },
    getCombRowInputDisplay (specId, optId, ridx, field) {
      const row = this.getCombRowRef(specId, optId, ridx)
      if (!row || !field) return ''
      const dk = this.combinationRowDraftStorageKey(row, field)
      if (dk && Object.prototype.hasOwnProperty.call(this.combinationFieldDraft, dk)) {
        const d = this.combinationFieldDraft[dk]
        if (d === null || d === undefined || d === '') return ''
        return String(d)
      }
      let v = row[field]
      if ((v === undefined || v === null || v === '') && field === 'nameEn') {
        v = row.name_en
      }
      if (v === undefined || v === null || v === '') return ''
      return String(v)
    },
    onCombRowAInputChangeByPath (specId, optId, ridx, field, payload) {
      if (field === 'name' || field === 'nameEn') {
        const raw = this.extractCombRowInputString(payload)
        if (raw === undefined) return
        const norm = raw.trim() === '' ? null : raw.trim()
        this.patchCombinationRowTextField(specId, optId, ridx, field, norm)
        return
      }
      const row = this.getCombRowRef(specId, optId, ridx)
      this.onCombRowAInputChangeValue(row, field, payload)
    },
    /** 从 a-input @change / 原生 input @input 取字符串（兼容 target / currentTarget） */
    extractCombRowInputString (payload) {
      if (payload === null || payload === undefined) return ''
      if (typeof payload === 'string') return payload
      if (typeof payload === 'number' && !Number.isNaN(payload)) return String(payload)
      if (typeof payload === 'object') {
        const t = payload.target != null ? payload.target : payload.currentTarget
        if (t && t.value !== undefined && t.value !== null) return String(t.value)
      }
      return undefined
    },
    /** 不可变更新组合行文本字段，保证 Vue2 深层响应式 + 草稿键与行对象一致 */
    patchCombinationRowTextField (specId, optId, ridx, field, norm) {
      if (field !== 'name' && field !== 'nameEn') return
      const k = String(specId)
      const oid = String(optId)
      const curSpec = { ...(this.specOptionCombinationSelectMap[k] || {}) }
      const rows = Array.isArray(curSpec[oid]) ? curSpec[oid].slice() : []
      const row = rows[ridx]
      if (!row) return
      const nextRow = { ...row, [field]: norm }
      rows[ridx] = nextRow
      curSpec[oid] = rows
      this.$set(this.specOptionCombinationSelectMap, k, curSpec)
      const dk = this.combinationRowDraftStorageKey(nextRow, field)
      if (dk) {
        this.$set(this.combinationFieldDraft, dk, norm)
      }
      if (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_GOODS_COMB') === '1') {
        // eslint-disable-next-line no-console
        console.log('[goods comb patch]', { field, norm, rowId: nextRow.id, _clientCid: nextRow._clientCid, dk })
      }
    },
    /** v-model 已写入 row 后，把当前值同步到草稿 Map，提交时 pickCombinationFieldForSubmit 可读到 */
    syncCombDraftFromRow (row, field) {
      if (!row || !field) return
      const raw = row[field]
      const s = raw === undefined || raw === null ? '' : String(raw)
      const val = s.trim() === '' ? null : s
      const dk = this.combinationRowDraftStorageKey(row, field)
      if (dk) {
        this.$set(this.combinationFieldDraft, dk, val)
      }
    },
    /**
     * ant-design-vue a-input 的 @change：Vue2 里不能写 @change.value（会被当成 change + 修饰符，参数变成 InputEvent，出现 [object InputEvent]）。
     * 这里从事件 e.target.value 取字符串（若已是字符串则直接用），再 $set 行 + 草稿。
     */
    onCombRowAInputChangeValue (row, field, payload) {
      if (!row || !field) return
      let raw = ''
      if (payload === null || payload === undefined) {
        raw = ''
      } else if (typeof payload === 'string') {
        raw = payload
      } else if (typeof payload === 'number' && !Number.isNaN(payload)) {
        raw = String(payload)
      } else if (typeof payload === 'object' && payload.target != null && payload.target.value !== undefined) {
        raw = String(payload.target.value)
      } else {
        return
      }
      const norm = raw.trim() === '' ? null : raw.trim()
      this.$set(row, field, norm)
      const dk = this.combinationRowDraftStorageKey(row, field)
      if (dk) {
        this.$set(this.combinationFieldDraft, dk, norm)
      }
      if (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_GOODS_COMB') === '1') {
        // eslint-disable-next-line no-console
        console.log('[goods comb input]', { field, norm, rowId: row.id, _clientCid: row._clientCid, dk })
      }
    },
    nextCombDraftClientId () {
      this._combDraftSeq = (this._combDraftSeq || 0) + 1
      return this._combDraftSeq
    },
    /** 组合行 zb_goods_combination.id，统一成字符串，避免草稿键 gid 与 JSON 大整型 object 不一致 */
    normalizeGoodsCombinationRowId (id) {
      if (id === null || id === undefined || id === '') return null
      if (typeof id === 'object') {
        try {
          const s = String(id)
          return s === '[object Object]' ? null : s
        } catch (e) {
          return null
        }
      }
      return String(id)
    },
    hasCombinationRowServerId (row) {
      if (!row || row.id === null || row.id === undefined || row.id === '') return false
      if (typeof row.id === 'object') return false
      return true
    },
    ensureCombinationRowClientId (row) {
      if (!row || this.hasCombinationRowServerId(row)) return
      if (row._clientCid == null) {
        this.$set(row, '_clientCid', this.nextCombDraftClientId())
      }
    },
    /** 草稿 Map 的 key，与提交时 pick 逻辑一致 */
    combinationRowDraftStorageKey (row, field) {
      if (!row || !field) return null
      if (this.hasCombinationRowServerId(row)) {
        return `gid_${String(row.id)}::${field}`
      }
      this.ensureCombinationRowClientId(row)
      return `cid_${row._clientCid}::${field}`
    },
    /** 提交时读取 name / nameEn：优先草稿（用户刚输入），否则读行对象 */
    pickCombinationFieldForSubmit (g, field) {
      if (!g || !field) return null
      const fromRow = () => {
        if (field === 'nameEn') {
          return g.nameEn != null && g.nameEn !== '' ? g.nameEn : g.name_en
        }
        return g[field]
      }
      let dk
      if (this.hasCombinationRowServerId(g)) {
        dk = `gid_${String(g.id)}::${field}`
      } else if (g._clientCid != null) {
        dk = `cid_${g._clientCid}::${field}`
      } else {
        return fromRow()
      }
      if (Object.prototype.hasOwnProperty.call(this.combinationFieldDraft, dk)) {
        const v = this.combinationFieldDraft[dk]
        if (v === undefined) return fromRow()
        if (v === null || v === '') return null
        const t = String(v).trim()
        return t === '' ? null : t
      }
      return fromRow()
    },
    emptyCombinationRow (overrides) {
      const o = Object.assign({
        id: null,
        name: null,
        nameEn: null,
        sort: 0,
        optionIdList: [],
        isDefault: false
      }, overrides || {})
      o.id = this.normalizeGoodsCombinationRowId(o.id)
      if (this.hasCombinationRowServerId(o)) {
        if (o._clientCid !== undefined) delete o._clientCid
        return o
      }
      if (o._clientCid == null) {
        o._clientCid = this.nextCombDraftClientId()
      }
      return o
    },
    addCombinationRow (specId, optionId) {
      const k = String(specId)
      const oid = String(optionId)
      if (!this.specOptionCombinationSelectMap[k] || typeof this.specOptionCombinationSelectMap[k] !== 'object') {
        this.$set(this.specOptionCombinationSelectMap, k, {})
      }
      const cur = { ...(this.specOptionCombinationSelectMap[k] || {}) }
      const rows = Array.isArray(cur[oid]) ? cur[oid].slice() : []
      rows.push(this.emptyCombinationRow())
      cur[oid] = rows
      this.$set(this.specOptionCombinationSelectMap, k, cur)
      this.loadAllSpecOptionOptions()
    },
    removeCombinationRow (specId, optionId, idx) {
      const k = String(specId)
      const oid = String(optionId)
      const cur = { ...(this.specOptionCombinationSelectMap[k] || {}) }
      const rows = Array.isArray(cur[oid]) ? cur[oid].slice() : []
      const removed = rows[idx]
      if (removed) {
        if (this.hasCombinationRowServerId(removed)) {
          const gid = String(removed.id)
          this.$delete(this.combinationFieldDraft, `gid_${gid}::name`)
          this.$delete(this.combinationFieldDraft, `gid_${gid}::nameEn`)
        } else if (removed._clientCid != null) {
          this.$delete(this.combinationFieldDraft, `cid_${removed._clientCid}::name`)
          this.$delete(this.combinationFieldDraft, `cid_${removed._clientCid}::nameEn`)
        }
      }
      rows.splice(idx, 1)
      cur[oid] = rows
      this.$set(this.specOptionCombinationSelectMap, k, cur)
    },
    onClose () {
      this.open = false
    },
    // 取消按钮
    cancel () {
      this.open = false
      this.reset()
    },
    // 表单重置
    reset () {
      this.formType = 1
      this.form = {
        id: null,
        categoryId: null,
        type: 0,
        name: null,
        nameEn: null,
        machineId: null,
        printMultiplesId: null,
        specialMultipleIds: null,
        toolIds: null,
        colorAdjustment: null,
        packagingFee: null,
        sealStripRemaining: 0,
        // installationFee: 0,
        passRate: null,
        compositeMaterials: null,
        machineTuning: null,
        compositeCost: null,
        machineCost: null,
        specIdList: [],
        specOptionSelectList: [],
        url: null,
        description: null,
        descriptionEn: null,
        content: null,
        contentEn: null,
        sort: null,
        status: 1,
        createTime: null,
        remark: null,
      }
      this.specOptionLoadingMap = {}
      this.specOptionMap = {}
      this.specSelectedOptionMap = {}
      this.specSortMap = {}
      this.specOptionCombinationMap = {}
      this.specOptionCombinationSelectMap = {}
      this.combinationFieldDraft = {}
      this._combDraftSeq = 0
      this.specOptionTypeMap = {}
      this.specOptionSortMap = {}
      this.associationList = []
      this.toolIdsSelected = []
      this.compositeMaterialsSelected = []
      this.machineTuningSelected = []
      this.specialMultipleIdsSelected = []
    },
    /** 新增按钮操作 */
    handleAdd (row) {
      this.reset()
      this.loadCategoryOptions()
      this.loadSpecOptions()
      this.loadMachineOptions('')
      this.loadPrintMultiplesOptions()
      this.loadSpecialMultipleOptions()
      this.loadCuttingToolOptions('')
      this.loadCalculationSpecOptions()
      this.formType = 1
      this.open = true
      this.formTitle = this.$t('views.goods.title.add')
      this.$nextTick(() => {
        this.editorKey = Date.now()
      })
    },
    /** 修改按钮操作 */
    handleUpdate (row, ids) {
      this.reset()
      this.loadCategoryOptions()
      this.loadSpecOptions()
      this.loadMachineOptions('')
      this.loadPrintMultiplesOptions()
      this.loadSpecialMultipleOptions()
      this.loadCuttingToolOptions('')
      this.loadCalculationSpecOptions()
      this.formType = 2
      const id = row ? row.id : ids
      getGoods({"id":id}).then(response => {
        this.form = response.data
        if (!this.form || typeof this.form !== 'object') {
          this.form = {}
        }
        // 商品类型：0-自立袋 1-三边封；缺省按自立袋
        const typeRaw = this.form.type != null && this.form.type !== '' ? Number(this.form.type) : 0
        this.$set(this.form, 'type', typeRaw === 1 ? 1 : 0)
        // 英文名称/描述/详情（camelCase 或 snake_case 回显）
        const nameEnRaw = this.form.nameEn != null && this.form.nameEn !== '' ? this.form.nameEn : this.form.name_en
        const descriptionEnRaw = this.form.descriptionEn != null && this.form.descriptionEn !== '' ? this.form.descriptionEn : this.form.description_en
        const contentEnRaw = this.form.contentEn != null && this.form.contentEn !== '' ? this.form.contentEn : this.form.content_en
        this.$set(this.form, 'nameEn', nameEnRaw != null && String(nameEnRaw).trim() !== '' ? String(nameEnRaw) : null)
        this.$set(this.form, 'descriptionEn', descriptionEnRaw != null && String(descriptionEnRaw).trim() !== '' ? String(descriptionEnRaw) : null)
        this.$set(this.form, 'contentEn', contentEnRaw != null && String(contentEnRaw).trim() !== '' ? String(contentEnRaw) : null)
        // 确保新增字段具备 Vue2 响应式，避免 a-input-number 出现“无法输入/不生效”
        if (this.form.compositeCost === undefined) {
          this.$set(this.form, 'compositeCost', null)
        }
        if (this.form.machineCost === undefined) {
          this.$set(this.form, 'machineCost', null)
        }
        if (this.form.machineId === undefined) {
          this.form.machineId = null
        }
        if (this.form.printMultiplesId === undefined) {
          this.$set(this.form, 'printMultiplesId', null)
        }
        const pmRaw = (this.form.printMultiplesId != null && this.form.printMultiplesId !== '')
          ? this.form.printMultiplesId
          : this.form.print_multiples_id
        this.$set(this.form, 'printMultiplesId', pmRaw !== undefined && pmRaw !== null && pmRaw !== '' ? pmRaw : null)
        const smRaw = this.form.specialMultipleIds != null && this.form.specialMultipleIds !== ''
          ? this.form.specialMultipleIds
          : this.form.special_multiple_ids
        this.$set(this.form, 'specialMultipleIds', smRaw != null && String(smRaw).trim() !== '' ? String(smRaw).trim() : null)
        this.specialMultipleIdsSelected = this.parseToolIdsStringToArray(this.form.specialMultipleIds)
        if (this.form.packagingFee === undefined) {
          this.$set(this.form, 'packagingFee', null)
        }
        let sealRemainingRaw = this.form.sealStripRemaining
        if (sealRemainingRaw === undefined || sealRemainingRaw === null || sealRemainingRaw === '') {
          sealRemainingRaw = this.form.seal_strip_remaining
        }
        this.$set(this.form, 'sealStripRemaining', this.normalizeSealStripRemaining(sealRemainingRaw))
        // let installationFeeRaw = this.form.installationFee
        // if (installationFeeRaw === undefined || installationFeeRaw === null || installationFeeRaw === '') {
        //   installationFeeRaw = this.form.installation_fee
        // }
        // this.$set(this.form, 'installationFee', this.parseGoodsDecimal2(installationFeeRaw != null && installationFeeRaw !== '' ? installationFeeRaw : 0))
        if (this.form.toolIds === undefined || this.form.toolIds === null) {
          this.form.toolIds = null
        } else {
          this.form.toolIds = String(this.form.toolIds).trim() || null
        }
        this.toolIdsSelected = this.parseToolIdsStringToArray(this.form.toolIds)
        const cmRaw = this.form.compositeMaterials != null && this.form.compositeMaterials !== ''
          ? this.form.compositeMaterials
          : this.form.composite_materials
        const mtRaw = this.form.machineTuning != null && this.form.machineTuning !== ''
          ? this.form.machineTuning
          : this.form.machine_tuning
        this.form.compositeMaterials = cmRaw != null && String(cmRaw).trim() !== '' ? String(cmRaw).trim() : null
        this.form.machineTuning = mtRaw != null && String(mtRaw).trim() !== '' ? String(mtRaw).trim() : null
        this.compositeMaterialsSelected = this.parseToolIdsStringToArray(this.form.compositeMaterials)
        this.machineTuningSelected = this.parseToolIdsStringToArray(this.form.machineTuning)
        const colorAdjRaw = this.form.colorAdjustment != null && this.form.colorAdjustment !== ''
          ? this.form.colorAdjustment
          : this.form.color_adjustment
        const packagingFeeRaw = this.form.packagingFee != null && this.form.packagingFee !== ''
          ? this.form.packagingFee
          : this.form.packaging_fee
        const passRateRaw = this.form.passRate != null && this.form.passRate !== ''
          ? this.form.passRate
          : this.form.pass_rate
        this.form.colorAdjustment = this.parseGoodsDecimal2(colorAdjRaw)
        this.form.packagingFee = this.parseGoodsDecimal2(packagingFeeRaw)
        this.form.passRate = this.parseGoodsDecimal2(passRateRaw)
        const compositeCostRaw = this.form.compositeCost != null && this.form.compositeCost !== ''
          ? this.form.compositeCost
          : this.form.composite_cost
        const machineCostRaw = this.form.machineCost != null && this.form.machineCost !== ''
          ? this.form.machineCost
          : this.form.machine_cost
        this.$set(this.form, 'compositeCost', this.parseGoodsDecimal2(compositeCostRaw))
        this.$set(this.form, 'machineCost', this.parseGoodsDecimal2(machineCostRaw))
        if (!Array.isArray(this.form.specIdList)) {
          this.form.specIdList = this.form.specIdList ? [this.form.specIdList] : []
        }

        // 回显：specOptionSelectList -> specSelectedOptionMap
        const selectList = Array.isArray(this.form.specOptionSelectList) ? this.form.specOptionSelectList : []
        selectList.forEach(item => {
          if (!item || !item.specId) return
          const k = String(item.specId)
          const ids = Array.isArray(item.optionIdList) ? item.optionIdList.filter(Boolean) : []
          this.$set(this.specSelectedOptionMap, k, ids)
          if (item.sort !== undefined && item.sort !== null) {
            this.$set(this.specSortMap, k, item.sort)
          }
          const combList = Array.isArray(item.combinationList) ? item.combinationList : []
          const combSwitchMap = {}
          const combSelectMap = {}
          const optSortMap = {}
          const optTypeMap = {}
          combList.forEach(c => {
            if (!c || !c.optionId) return
            if (c.isCombination) {
              combSwitchMap[String(c.optionId)] = true
            }
            optTypeMap[String(c.optionId)] = (c.type === 1 || c.type === '1') ? 1 : 0
            const groups = Array.isArray(c.specOptionsGroupList) ? c.specOptionsGroupList : []
            const cleanGroups = groups.map(g => {
              if (g && typeof g === 'object') {
                const nameEnRaw = g.nameEn != null && g.nameEn !== '' ? g.nameEn : g.name_en
                return {
                  id: this.normalizeGoodsCombinationRowId(g.id),
                  name: g.name || null,
                  nameEn: nameEnRaw != null && String(nameEnRaw).trim() !== '' ? String(nameEnRaw) : null,
                  sort: g.sort !== undefined && g.sort !== null ? g.sort : 0,
                  optionIdList: Array.isArray(g.optionIdList) ? g.optionIdList.filter(Boolean) : [],
                  isDefault: !!g.isDefault
                }
              }
              // 兼容旧数据：数组
              return this.emptyCombinationRow({ optionIdList: Array.isArray(g) ? g.filter(Boolean) : [], sort: 0, isDefault: false })
            }).filter(g => Array.isArray(g.optionIdList) && g.optionIdList.length)
            combSelectMap[String(c.optionId)] = cleanGroups
            if (c.sort !== undefined && c.sort !== null) {
              optSortMap[String(c.optionId)] = c.sort
            }
          })
          this.$set(this.specOptionCombinationMap, k, combSwitchMap)
          this.$set(this.specOptionCombinationSelectMap, k, combSelectMap)
          this.$set(this.specOptionTypeMap, k, optTypeMap)
          this.$set(this.specOptionSortMap, k, optSortMap)
        })

        // 触发加载选项并补齐默认
        this.onSpecIdListChange(this.form.specIdList)
        this.loadAllSpecOptionOptions()

        // 关联设置回显（随商品详情一起返回 associationList）
        const assoc = (this.form && Array.isArray(this.form.associationList)) ? this.form.associationList : []
        this.associationList = assoc.map(it => ({
          id: it.id,
          goodId: it.goodId,
          specId: it.specId,
          specOptionId: it.specOptionId,
          twoSpecId: it.twoSpecId,
          specOptionsList: it.specOptionsList ? String(it.specOptionsList).split(',').map(x => String(x).trim()).filter(Boolean) : [],
          defaultOption: it.defaultOption ? String(it.defaultOption).trim() : null,
          sort: it.sort !== undefined && it.sort !== null ? it.sort : 0,
          status: it.status !== undefined && it.status !== null ? it.status : 1,
          _rowKey: `assoc-${it.id || Date.now()}-${Math.random().toString(16).slice(2)}`
        }))
        this.pruneAssociationList()
        this.open = true
        this.formTitle = this.$t('views.goods.title.edit')
        this.$nextTick(() => {
          this.editorKey = Date.now()
        })
      })
    },
    /** 提交按钮 */
    submitForm: function () {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.submitLoading = true
          this.syncFormToolIdsFromSelected()
          this.syncFormCompositeMaterialsFromSelected()
          this.syncFormMachineTuningFromSelected()
          this.syncFormSpecialMultipleIdsFromSelected()
          this.form.colorAdjustment = this.parseGoodsDecimal2(this.form.colorAdjustment)
          this.form.packagingFee = this.parseGoodsDecimal2(this.form.packagingFee)
          this.form.passRate = this.parseGoodsDecimal2(this.form.passRate)
          this.form.compositeCost = this.parseGoodsDecimal2(this.form.compositeCost)
          this.form.machineCost = this.parseGoodsDecimal2(this.form.machineCost)
          this.form.sealStripRemaining = this.normalizeSealStripRemaining(this.form.sealStripRemaining)
          // this.form.installationFee = this.parseGoodsDecimal2(this.form.installationFee != null && this.form.installationFee !== '' ? this.form.installationFee : 0)
          if (this.form.printMultiplesId === '' || this.form.printMultiplesId === undefined) {
            this.form.printMultiplesId = null
          }
          if (this.form.packagingFee === '' || this.form.packagingFee === undefined) {
            this.form.packagingFee = null
          }

          // 组装 specOptionSelectList（按 spec 维度）
          const specIdList = Array.isArray(this.form.specIdList) ? this.form.specIdList.filter(Boolean) : []
          this.form.specOptionSelectList = specIdList.map(specId => {
            const k = String(specId)
            const optionIdList = Array.isArray(this.specSelectedOptionMap[k]) ? this.specSelectedOptionMap[k].filter(Boolean) : []
            const sort = this.specSortMap[k] !== undefined && this.specSortMap[k] !== null ? this.specSortMap[k] : 0
            const combMap = this.specOptionCombinationMap[k] || {}
            const selectMap = this.specOptionCombinationSelectMap[k] || {}
            const optSortMap = this.specOptionSortMap[k] || {}
            const optTypeMap = this.specOptionTypeMap[k] || {}
            const combinationList = optionIdList.map(id => {
              const isCombination = !!combMap[String(id)]
              const groups = Array.isArray(selectMap[String(id)]) ? selectMap[String(id)] : []
              const cleanGroups = groups.map(g => {
                if (g && typeof g === 'object') {
                  const namePick = this.pickCombinationFieldForSubmit(g, 'name')
                  const nameEnPick = this.pickCombinationFieldForSubmit(g, 'nameEn')
                  const nameVal = namePick == null || String(namePick).trim() === '' ? null : String(namePick).trim()
                  const nameEnVal = nameEnPick == null || String(nameEnPick).trim() === '' ? null : String(nameEnPick).trim()
                  return {
                    id: this.normalizeGoodsCombinationRowId(g.id),
                    name: nameVal,
                    nameEn: nameEnVal,
                    sort: g.sort !== undefined && g.sort !== null ? g.sort : 0,
                    optionIdList: Array.isArray(g.optionIdList) ? g.optionIdList.filter(Boolean) : [],
                    isDefault: !!g.isDefault
                  }
                }
                return this.emptyCombinationRow({ optionIdList: Array.isArray(g) ? g.filter(Boolean) : [], sort: 0, isDefault: false })
              }).filter(g => Array.isArray(g.optionIdList) && g.optionIdList.length)
              const sortVal = optSortMap[String(id)] !== undefined && optSortMap[String(id)] !== null ? optSortMap[String(id)] : 0
              const typeVal = optTypeMap[String(id)] !== undefined && optTypeMap[String(id)] !== null ? optTypeMap[String(id)] : 0
              return { optionId: id, sort: sortVal, isCombination: isCombination, type: typeVal, specOptionsGroupList: cleanGroups }
            })
            return { specId: specId, sort: sort, combinationList: combinationList, optionIdList: optionIdList }
          })

          // 关联设置：随商品一起提交（由后端增量同步 zb_goods_association）
          this.pruneAssociationList()
          const curList = Array.isArray(this.associationList) ? this.associationList : []
          this.form.associationList = curList.map((it, idx) => ({
            id: it && it.id ? it.id : null,
            specId: it ? it.specId : null,
            specOptionId: it ? it.specOptionId : null,
            twoSpecId: it ? it.twoSpecId : null,
            specOptionsList: it && Array.isArray(it.specOptionsList) ? it.specOptionsList.map(x => String(x)).filter(Boolean).join(',') : null,
            defaultOption: it && it.defaultOption ? String(it.defaultOption) : null,
            sort: it && it.sort !== undefined && it.sort !== null ? it.sort : idx,
            status: it && it.status !== undefined && it.status !== null ? it.status : 1
          }))

          if (this.form.id !== undefined && this.form.id !== null) {
            updateGoods(this.form).then(response => {

              if(response.success) {
                this.$message.success(this.$t('views.goods.msg.updateOk'),3)
                this.open = false
                this.$emit('ok')
              } else {
                let messageInfo = response.message || response.msg || this.$t('views.goods.msg.updateFail');
                this.$message.error(messageInfo);
              }

            }).finally(() => {
              this.submitLoading = false
            })
          } else {
            addGoods(this.form).then(response => {
              if(response.success) {
                this.$message.success(this.$t('views.goods.msg.addOk'),3)
                this.open = false
                this.$emit('ok')
              } else {
                let messageInfo = response.message || response.msg || this.$t('views.goods.msg.addFail');
                this.$message.error(messageInfo);
              }

            }).finally(() => {
              this.submitLoading = false
            })
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style scoped>
.spec-card :deep(.ant-card-head) {
  padding: 0 12px;
}
.spec-card :deep(.ant-card-head-title) {
  padding: 10px 0;
}
.spec-card :deep(.ant-card-extra) {
  padding: 10px 0;
}
.spec-card :deep(.ant-card-head-wrapper) {
  flex-wrap: wrap;
  row-gap: 8px;
  column-gap: 16px;
}
.spec-card :deep(.ant-card-head-title) {
  min-width: 260px;
}
.spec-card :deep(.ant-card-body) {
  padding: 12px;
  background: #fafafa;
}

.spec-card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.spec-card-title__left {
  font-weight: 600;
  min-width: 0;
}
.spec-card-title__calc {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(82, 196, 26, 0.12);
  color: #389e0d;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
}
.spec-card-title__tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
}
.spec-card-title__tag--custom {
  background: rgba(24, 144, 255, 0.10);
  color: #096dd9;
}
.spec-card-title__tag--pro {
  background: rgba(250, 173, 20, 0.14);
  color: #ad6800;
}
.spec-card-title__right {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}
.spec-card-title__label {
  color: rgba(0,0,0,0.45);
}

.spec-option-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px 10px;
  width: 100%;
  justify-items: stretch;
}

.spec-option-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fff;
  transition: all 0.15s ease;
}
.spec-option-row:hover {
  border-color: #91d5ff;
  box-shadow: 0 0 0 2px rgba(24,144,255,0.08);
}
.spec-option-row--checked {
  border-color: #1890ff;
  background: rgba(24,144,255,0.04);
}
.spec-option-row__checkbox {
  margin-top: 2px;
}
.spec-option-row__body {
  flex: 1;
  min-width: 0;
}
.spec-option-footer {
  margin-top: 10px;
  color: rgba(0,0,0,0.45);
  font-size: 12px;
  text-align: right;
}

.spec-option-name {
  display: inline-block;
  white-space: nowrap;
  overflow: visible;
  vertical-align: middle;
}
.spec-option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}
.spec-option-comb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.spec-option-comb__label {
  color: rgba(0,0,0,0.45);
  font-size: 12px;
}
.spec-option-comb-select {
  margin-top: 10px;
  margin-left: 22px; /* 缩进，形成层级 */
  padding: 10px 10px;
  background: #f6f9ff;
  border: 1px dashed #cfe3ff;
  border-radius: 8px;
}
.spec-option-comb-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.spec-option-comb-row__title {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 8px;
  border-radius: 4px;
  background: rgba(0,0,0,0.04);
  color: rgba(0,0,0,0.65);
  font-size: 12px;
  white-space: nowrap;
}
.spec-option-comb-row .spec-option-comb__label {
  display: inline-flex;
  align-items: center;
  height: 32px;
  white-space: nowrap;
}
.spec-option-comb-row :deep(.ant-select-selection) {
  min-height: 32px;
}
.spec-option-comb-row :deep(.ant-select-selection__rendered) {
  line-height: 30px;
}
.spec-option-comb-row :deep(.ant-input-number) {
  height: 32px;
}
.spec-option-comb-row :deep(.ant-input-number-input) {
  height: 30px;
}
.spec-option-comb-row__delete {
  padding: 0;
  height: 32px;
  line-height: 32px;
}
/* 组合多选框占满宽度，标签更紧凑 */
.spec-option-comb-select :deep(.ant-select) {
  width: 100%;
}
.spec-option-comb-select :deep(.ant-select-selection--multiple) {
  min-height: 32px;
}
.spec-option-comb-select :deep(.ant-select-selection__rendered) {
  margin-left: 6px;
  margin-right: 6px;
}

/* ant-checkbox-group 宽度修正 */
:deep(.ant-checkbox-group) {
  display: block;
  width: 100%;
}
</style>
